import { useEffect, useRef, useState } from 'react';
import { toast } from 'sonner';
import { api } from '../../api/client';
import { SkeletonRows } from '../../components/skeleton';
import { Pagination } from '../../components/pagination';
import { PageHeader } from '../../components/page-header';
import { EmptyState } from '../../components/empty-state';
import { TableSearch, useDebouncedValue } from '../../components/table-search';
import { extractErr } from '../../api/error-message';

// Wired to GET /admin/error-logs (+ /summary). Every request the API rejects is
// recorded server-side, and the apps additionally report failures that never
// reach the API at all — a direct-to-S3 upload, a parse error, a timeout. Those
// arrive as source = CLIENT and are the only trace such a failure leaves.

const PER_PAGE = 25;

interface ErrorRow {
  id: string;
  source: 'API' | 'CLIENT';
  method: string;
  path: string;
  query: string | null;
  status: number;
  code: string;
  message: string;
  stack: string | null;
  details: unknown;
  actorType: string | null;
  actorId: string | null;
  actorName: string | null;
  deviceId: string | null;
  appVersion: string | null;
  correlationId: string | null;
  ip: string | null;
  createdAt: string;
}

interface Summary {
  sinceHours: number;
  total: number;
  serverFaults: number;
  clientReports: number;
  affectedDevices: number;
  topCodes: Array<{ code: string; count: number }>;
}

const SOURCE_TABS = ['ALL', 'API', 'CLIENT'] as const;
type SourceKey = (typeof SOURCE_TABS)[number];

function statusStyle(status: number, source: string): string {
  if (source === 'CLIENT') return 'badge-warning';
  if (status >= 500) return 'badge-danger';
  if (status === 429) return 'badge-warning';
  if (status >= 400) return '';
  return '';
}

const ACTOR_LABEL: Record<string, string> = {
  CUSTOMER: 'Customer',
  SELLER: 'Seller',
  DELIVERY_PARTNER: 'Partner',
  ADMIN: 'Admin',
};

export function ErrorLogsPage() {
  const [rows, setRows] = useState<ErrorRow[]>([]);
  const [summary, setSummary] = useState<Summary | null>(null);
  const [source, setSource] = useState<SourceKey>('ALL');
  const [search, setSearch] = useState('');
  // Server-searched (the log is paged by the API) across every column below:
  // date, status ("App" for app reports), method + path, user name / #id /
  // account type, device, app version, error code and message.
  const debouncedSearch = useDebouncedValue(search.trim(), 300);
  const [deviceFilter, setDeviceFilter] = useState<string | null>(null);
  const [page, setPage] = useState(1);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(true);
  const [expanded, setExpanded] = useState<string | null>(null);

  // Only the newest request may write rows: typing fires several, and a slow
  // early one must not overwrite the results of the query now in the box.
  const latestRequest = useRef(0);
  const load = async () => {
    const requestId = ++latestRequest.current;
    setLoading(true);
    try {
      const params: Record<string, string | number> = { page, limit: PER_PAGE };
      if (source !== 'ALL') params.source = source;
      if (debouncedSearch) params.q = debouncedSearch;
      if (deviceFilter) params.deviceId = deviceFilter;
      const [list, sum] = await Promise.all([
        api.get<{ data: ErrorRow[]; pagination: { total: number } }>('/admin/error-logs', { params }),
        api.get<Summary>('/admin/error-logs/summary', { params: { sinceHours: 24 } }),
      ]);
      if (requestId !== latestRequest.current) return;
      setRows(list.data.data);
      setTotal(list.data.pagination.total);
      setSummary(sum.data);
    } catch (err) {
      toast.error(extractErr(err));
    } finally {
      if (requestId === latestRequest.current) setLoading(false);
    }
  };

  useEffect(() => { void load(); /* eslint-disable-next-line react-hooks/exhaustive-deps */ }, [page, source, deviceFilter, debouncedSearch]);
  // Reset to the first page whenever the filter changes, so a search on page 4
  // does not silently return nothing.
  useEffect(() => { setPage(1); }, [source, deviceFilter, debouncedSearch]);

  const totalPages = Math.max(1, Math.ceil(total / PER_PAGE));
  const searching = debouncedSearch.length > 0;

  return (
    <div className="space-y-6">
      <PageHeader
        title="Error log"
        description="Failures from the API and from the apps, with the account and device that hit them"
      />

      {summary && (
        <div className="grid grid-cols-2 gap-4 md:grid-cols-4">
          <SummaryTile label="Errors (24h)" value={summary.total} />
          <SummaryTile label="Server faults" value={summary.serverFaults} tone={summary.serverFaults > 0 ? 'danger' : undefined} />
          <SummaryTile label="App reports" value={summary.clientReports} tone={summary.clientReports > 0 ? 'warning' : undefined} />
          {/* The question a support ticket actually asks: one handset, or everyone? */}
          <SummaryTile label="Devices affected" value={summary.affectedDevices} />
        </div>
      )}

      <div className="flex flex-wrap items-center gap-2">
        {SOURCE_TABS.map((t) => (
          <button
            key={t}
            type="button"
            onClick={() => setSource(t)}
            aria-pressed={source === t}
            className="fchip"
          >
            {t === 'ALL' ? 'All' : t === 'API' ? 'Server' : 'App'}
          </button>
        ))}
      </div>

      <TableSearch
        value={search}
        onChange={setSearch}
        placeholder="Search by path, error, code, user, device, status or date"
        resultCount={!loading && searching ? total : undefined}
      />

      {deviceFilter && (
        <div className="alert alert-warning items-center text-[13.5px]">
          <span>Showing one device only: <span className="font-mono">{deviceFilter.slice(0, 12)}…</span></span>
          <button type="button" onClick={() => setDeviceFilter(null)} className="btn btn-secondary btn-sm ml-auto">
            Show all devices
          </button>
        </div>
      )}

      <div className="panel overflow-hidden">
        {loading ? (
          <SkeletonRows cols={6} />
        ) : rows.length === 0 && searching ? (
          <EmptyState
            bare
            icon="search_off"
            title={`No matches for “${debouncedSearch}”`}
            body="Try another word, or clear the search to see every error in this view."
            action={<button type="button" onClick={() => setSearch('')} className="btn btn-secondary">Clear search</button>}
          />
        ) : rows.length === 0 ? (
          <EmptyState
            bare
            icon="task_alt"
            title="Nothing failing in this view"
            body="Rejected API requests and failures reported by the apps show up here as they happen."
          />
        ) : (
          <div className="overflow-x-auto">
          <table className="data-table">
            <thead>
              <tr>
                <th>When</th>
                <th>Status</th>
                <th>Where</th>
                <th>User</th>
                <th>Device</th>
                <th>Error</th>
              </tr>
            </thead>
            <tbody>
              {rows.map((r) => (
                <>
                  <tr
                    key={r.id}
                    onClick={() => setExpanded(expanded === r.id ? null : r.id)}
                    className={`cursor-pointer ${expanded === r.id ? 'is-selected' : ''}`}
                  >
                    <td className="whitespace-nowrap text-[13.5px] text-ink-500">
                      {new Date(r.createdAt).toLocaleString('en-IN', { dateStyle: 'short', timeStyle: 'medium' })}
                    </td>
                    <td>
                      <span className={`badge font-tabular ${statusStyle(r.status, r.source)}`}>
                        {r.source === 'CLIENT' ? 'App' : r.status}
                      </span>
                    </td>
                    <td className="sku max-w-[220px] truncate text-ink-700">
                      {r.source === 'API' ? `${r.method} ${r.path}` : r.path}
                    </td>
                    <td className="text-ink-700">
                      {r.actorName ?? (r.actorId ? `#${r.actorId}` : '—')}
                      {r.actorType && (
                        <span className="block text-[12.5px] text-ink-500">
                          {ACTOR_LABEL[r.actorType] ?? r.actorType}
                        </span>
                      )}
                    </td>
                    <td className="sku text-ink-500">
                      {r.deviceId ? (
                        <button
                          type="button"
                          onClick={(e) => { e.stopPropagation(); setDeviceFilter(r.deviceId); }}
                          className="underline hover:text-admin-ink"
                          title="Show only this device"
                        >
                          {r.deviceId.slice(0, 8)}…
                        </button>
                      ) : '—'}
                      {r.appVersion && <span className="block font-sans text-[12.5px] text-ink-500">v{r.appVersion}</span>}
                    </td>
                    <td className="max-w-[280px] text-ink-700">
                      <span className="sku font-semibold text-ink-900">{r.code}</span>
                      <span className="block truncate text-[13.5px] text-ink-500">{r.message}</span>
                    </td>
                  </tr>
                  {expanded === r.id && (
                    <tr key={`${r.id}-detail`} className="bg-surface-sunk hover:bg-surface-sunk">
                      <td colSpan={6} className="!py-4">
                        <dl className="grid grid-cols-2 gap-x-6 gap-y-3 text-[13.5px] md:grid-cols-4">
                          <Detail label="Correlation ID" value={r.correlationId} mono />
                          <Detail label="IP" value={r.ip} mono />
                          <Detail label="Device ID" value={r.deviceId} mono />
                          <Detail label="App version" value={r.appVersion} />
                          <Detail label="Query" value={r.query} mono />
                        </dl>
                        <p className="mt-3 whitespace-pre-wrap text-[13.5px] text-ink-700">{r.message}</p>
                        {r.details != null && (
                          <pre className="mt-3 max-h-48 overflow-auto rounded-md bg-ink-900 p-3.5 font-mono text-[12.5px] text-ink-100">
                            {JSON.stringify(r.details, null, 2)}
                          </pre>
                        )}
                        {r.stack && (
                          <pre className="mt-3 max-h-64 overflow-auto rounded-md bg-ink-900 p-3.5 font-mono text-[12.5px] text-ink-100">
                            {r.stack}
                          </pre>
                        )}
                      </td>
                    </tr>
                  )}
                </>
              ))}
            </tbody>
          </table>
          </div>
        )}
        {!loading && (
          <Pagination page={page} totalPages={totalPages} totalItems={total} perPage={PER_PAGE} onPageChange={setPage} />
        )}
      </div>
    </div>
  );
}

function SummaryTile({ label, value, tone }: { label: string; value: number; tone?: 'danger' | 'warning' }) {
  const toneClass = tone === 'danger' ? 'text-danger-ink' : tone === 'warning' ? 'text-warning-ink' : 'text-ink-900';
  return (
    <div className="panel p-5">
      <p className="text-[13.5px] font-semibold text-ink-500">{label}</p>
      <p className={`mt-1.5 font-tabular text-kpi ${toneClass}`}>{value}</p>
    </div>
  );
}

function Detail({ label, value, mono }: { label: string; value: string | null; mono?: boolean }) {
  if (!value) return null;
  return (
    <div>
      <dt className="text-[12.5px] font-semibold text-ink-500">{label}</dt>
      <dd className={`mt-0.5 text-ink-900 ${mono ? 'sku break-all' : ''}`}>{value}</dd>
    </div>
  );
}
